
import {BehaviorSubject, from, Observable} from 'rxjs';
// import * as crypto from "crypto";
import {map} from 'rxjs/operators';
import {
  confirmSignIn,
  fetchUserAttributes,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  signUp,
  deleteUser
} from 'aws-amplify/auth';

export class AuthService {
  private loggedIn$ = new BehaviorSubject(false);
  public loggedIn = this.loggedIn$.asObservable();

  constructor() {
    this.isAuthenticated().then((loggedIn) => this.loggedIn$.next(loggedIn));
  }

// Get temporary access keys from the Identity Pool
  public async getAccessKeys(): Promise<any> {
    const authSession = await fetchAuthSession();
    return authSession.credentials;
  }

// Get Cognito username
  public getUserSub(): Observable<any> {
    return from(getCurrentUser()).pipe(
      map(authUser => (authUser.userId))
    );
  }
  public async getUserSubPromise() {
    return await getCurrentUser();
  }

// Get Cognito user attributes - observable
  public getUserAttributes() {
    return from(fetchUserAttributes()).pipe(
      map(attributes => (attributes))
    );
  }

// Get Cognito user attributes - promise
  public async getUserAttributesPromise() {
    return await fetchUserAttributes();
  }

  public async signUp(email: string) {
    try {
      const {isSignUpComplete, userId, nextStep} = await signUp({
        username: email,
        password: this.getRandomString(30),
        options: {
          userAttributes: {
            email,
            name: email
          }
        }
      });
      return isSignUpComplete;
    } catch (error: any) {
      console.error('error signing up:', error);
      return error;
    }
  }

  public async signIn(email: string) {
    try {
      const {nextStep} = await signIn({
        username: email,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP'
        }
      });
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
        return 'custom_challenge';
      } else {
        return 'unknown_error';
      }
    } catch (error: any) {
      if (error.message.includes('CreateAuthChallenge failed with error Missing email')) {
        return 'NoExistingUser';
      }
      else {
        console.error('error signing in', error);
        return error;
      }
    }
  }

  public async signOut() {
    try {
      await signOut();
      this.loggedIn$.next(false);
    } catch (error) {
      console.error('error signing out: ', error);
    }
  }

  public async answerCustomChallenge(answer: string) {
    try {
      const {
        isSignedIn,
        nextStep
      } = await confirmSignIn({challengeResponse: answer});
      this.loggedIn$.next(isSignedIn);
      if (isSignedIn) {
        // login successful
        return 'isSignedIn';
      }
      if ('additionalInfo' in nextStep) {
        // login failed, present the nextStep feedback (how many attempts left etc.).
        return nextStep.additionalInfo;
      }
    } catch (error: any) {
      if (error.name.includes('SignInException')) {
        console.log(error);
        return 'There was an issue, please try again.';
      }
      return error;
    }
  }

  public fetchAuthSession() {
    const getSession = async () => {
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('Tokens are undefined');
      }
      const {idToken, accessToken} = session.tokens;
      return idToken;
    }
    return getSession();
  }

// returns a boolean based on auth state
  public async isAuthenticated() {
    try {
      await this.fetchAuthSession();
      this.loggedIn$.next(true);
      return true;
    } catch {
      this.loggedIn$.next(false);
      return false;
    }
  }

// Get ID Token to make API calls when Cognito authentication is enabled on API Gateway
  public async getIDToken() {
    return await this.fetchAuthSession().then((result) => {
      return result;
    });
  }

  async deleteUser() {
    this.loggedIn$.next(false);
    return await deleteUser();
  }

// Generate a password to create user but never used as we have a custom auth challenge setup in Cognito
  public getRandomString(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  }
}
